import { EventEmitter, Injectable, signal, WritableSignal } from '@angular/core';
import { BuildingsCookieService } from './buildings-cookie.service';
import { environment } from '../../../../environments/environment';
import { lastValueFrom, Subject } from 'rxjs';
import { AccountInfo } from '../model/account-info';
import { HttpClient } from '@angular/common/http';
import { UserInfo } from '../model/user-info';
import { SharedUtils } from '../utilities/shared-utils';
import { EnumLegalEntityRole, LegalEntityBasicInfo } from '../model/legal-entity-basic-info';
import { LegalEntityInfo } from '../model/legal-entity-info';
import { CitizenInfo } from '../model/citizen-info';
import { EnumRoleFullIdentifier } from '../model/user-basic-info';
import { Router } from '@angular/router';
import { UserLegalEntityBasicDetail } from '../model/user-legal-entity-basic-detail';
import { EnumUserLegalEntityStatus, UserLegalEntityInfo } from '../model/user-legal-entity-info';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {

    private static readonly authBaseUrl: string = environment.buildingsBackendMembershipAddress + '/client/auth';
    private waitingSpinner: WritableSignal<boolean> = signal<boolean>(false);
    private headerInformation: WritableSignal<string> = signal<string>('');
    popupActivation: Subject<{type: string, message: string}> = new Subject<{type: string, message: string}>();
    private popupResult: Subject<boolean> = new Subject<boolean>();

    // private static readonly legalEntityBaseUrl: string = environment.buildingsBackendMembershipAddress + '/client/private/legal-entity';
    // private static readonly permissionBaseUrl: string = environment.membershipBackendAddress + '/api/permission';
    // private static readonly functionalityBaseUrl: string = environment.membershipBackendAddress + '/api/applicationFunctionality';
    // private static readonly componentBaseUrl: string = environment.membershipBackendAddress + '/api/applicationComponent';
    // private static readonly roleBaseUrl: string = environment.membershipBackendAddress + '/api/role';
    // private static constantsAllreadyPopulated: boolean = false;
    // private static permissions: Array<Permission> = new Array<Permission>();
    // private static readonly ROLES_NAME_BY_FUNCTIONALITY_NAME: Map<string, Set<string>> = new Map<string, Set<string>>();
    // public titleBarData: Subject<{title: string, offcanvas: boolean, imageName?: string}> = new Subject<{title: string, offcanvas: boolean, imageName?: string}>();
    // modalStandardMessage: Subject<string> = new Subject<string>();
    // userAuthenticated: boolean = false;
    // userAuthenticated: Subject<boolean> = new Subject<boolean>();
    // private userAuthenticated: WritableSignal<boolean>;

    constructor(private readonly cookieService: BuildingsCookieService, private readonly httpClient: HttpClient,
        private readonly router: Router) { }

    /******************************************************
     * LOGIN & LOGOUT METHODS
     ******************************************************/

    private login(userInfo: UserInfo): boolean {
        // console.log("PermissionService.login(UserInfo) - User info: " + JSON.stringify(userInfo));
        const result = this.cookieService.login(userInfo);
        if (result) {
            // console.debug("PermissionService.login: user authenticated successfully");
        } else {
            // console.error("PermissionService.login: user authentication failed");
        }
        this.setHeaderInformation();
        return result;
    }

    private logout(serverLoggedOut: boolean): void {
        this.cookieService.logout();
        this.setHeaderInformation();
        // console.debug("PermissionService.logout: user logged out on client. \nServer logout successful? " + serverLoggedOut);
    }

    updateClientUserInfo(newUserInfo: UserInfo): boolean {
        const result: boolean = this.cookieService.updateClientUserInfo(newUserInfo);
        if (result) {
            this.setHeaderInformation();
        } else {
            this.headerInformation.set('');
        }
        return result;
    }

    public checkUserAuthentication(): void {
       this.cookieService.checkAuthentication();
    }

    public isUserAuthenticated(): boolean {
        return this.cookieService.isAuthenticated();
    }

    public acceptCookies(): void {
        this.cookieService.acceptCookies();
    }

    public checkCookies(): boolean {
        return this.cookieService.checkCookies();
    }


    /******************************************************
     * PERMISSIONS MANAGEMENT METHODS
     ******************************************************/
    
    checkPermissions(roles:EnumRoleFullIdentifier[]): boolean {
        return this.cookieService.checkPermissions(roles);
    }

    getClientUserRoles(): Array<EnumRoleFullIdentifier> | undefined {
        return this.cookieService.getClientUserRoles();
    }
    
    /*
    checkAuthorization(): boolean {
        return this.cookieService.checkPermission();
        this.permissionService.checkAuthorization(environment.libraryModuleName + ".WineDenAddComponent");
    }
    */



    /******************************************************
     * MAIN METHODS TO RETREIVE USER DATA
     ******************************************************/

    public getClientUser(): UserInfo | undefined {
        if (true/*this.cookieService.isAuthenticated()*/) {
            return this.cookieService.getClientUser();
        }
        return undefined;
    }

    public getClientUserCitizen(): CitizenInfo | undefined {
        if (true) {
            return this.cookieService.getClientUserCitizen();
        }
        return undefined;
    }

    public getClientUserActiveLegalEntity(): LegalEntityInfo | undefined {
        if (true) {
            return this.cookieService.getClientUserActiveLegalEntity();
        }
        return undefined;
    }

    public getClientRelUserActiveLegalEntity(): UserLegalEntityInfo | undefined {
        if (true) {
            return this.cookieService.getClientRelUserActiveLegalEntity();
        }
        return undefined;
    }

    public getClientUserActiveLegalEntityBasicDetail(): UserLegalEntityBasicDetail {
        return this.cookieService.getClientUserActiveLegalEntityBasicDetail();
    }

    public getClientUserLegalEntitiesBasicInfo(): LegalEntityBasicInfo[] | undefined {
        if (true) {
            return this.cookieService.getClientUserLegalEntitiesBasicInfo();
        }
        return undefined;
    }

    /******************************************************
     * ENCRYPTION & CLIENT FINGERPRINT DATA
     ******************************************************/

    public getEncryptedClientDeviceFingerprint(applicationComponent: string, activeLegalEntityId: number): Promise<{encyptedFingerprint: string, encryptedActiveLegalEntityId: string}> {
        // console.log("PermissionService.getEncryptedClientDeviceFingerprint - applicationComponent: " + applicationComponent);
        return this.cookieService.getEncryptedClientDeviceFingerprint(applicationComponent, activeLegalEntityId);
    }

    public encryptData(data: string, encryptSalt: string): Promise<string> {
        return this.cookieService.encryptData(data, encryptSalt);
    }

    /*********************************
     * WAITING SPINNER METHODS
     *********************************/

    public isWaitingSpinner(): boolean {
        return this.waitingSpinner();
    }

    public setWaitingSpinner(waitingSpinner: boolean): void {
        this.waitingSpinner.set(waitingSpinner);
    }


    /*********************************
     * POPUP METHODS
     ********************************/

    public alert(message: string): Promise<boolean> {
        return new Promise<boolean>( (resolve) => {
            this.popupActivation.next({type: "alert", message: message});
            if( !this.popupResult.observed) {
                this.popupResult.subscribe( (result: boolean) => {
                    resolve(result);
                    // this.popupResult.complete();
                    // this.popupResult.unsubscribe();
                });
            } else {
                this.popupResult.forEach( (result: boolean) => {
                    resolve(result);
                });
            }
        });
    }

    public confirm(message: string): Promise<boolean> {
        return new Promise<boolean>( (resolve) => {
            this.popupActivation.next({type: "confirm", message: message});
            if( !this.popupResult.observed) {
                this.popupResult.subscribe( (result: boolean) => {
                    resolve(result);
                    // this.popupResult.complete();
                    // this.popupResult.unsubscribe();
                });
            } else {
                this.popupResult.forEach( (result: boolean) => {
                    resolve(result);
                });
            }
        });
    }

    public setPopupOutput(result: boolean): void {
        // console.debug("received POPUP RESULT: " + result);
        this.popupResult.next(result);
        this.popupActivation.next({type: "", message: ""});
    }

    /*********************************************
     * HEADER INFORMATION METHODS
     *********************************************/

    public getHeaderInformation(): string {
        return this.headerInformation();
    }

    public setHeaderInformation(): void {
        const userInfo: UserInfo | undefined = this.getClientUser();
        if (!userInfo || (userInfo.id ?? 0) < 1) {
            return;
        }
        if(userInfo.activeLegalEntityInfo && (userInfo.activeLegalEntityInfo?.id ?? 0) > 0 && userInfo.activeUserLegalEntityInfo && userInfo.activeUserLegalEntityInfo?.legalEntityRole &&
          userInfo.activeUserLegalEntityInfo?.statusId === EnumUserLegalEntityStatus.active && userInfo.citizenInfo && userInfo.citizenInfo.name && userInfo.citizenInfo.surname) {
            this.headerInformation.set( (userInfo.citizenInfo.name.substring(0, 1)) + ". " + userInfo.citizenInfo.surname + " - " + (userInfo.activeLegalEntityInfo.companyName ?? '') + " (" +
            SharedUtils.getEnumValue(EnumLegalEntityRole, userInfo.activeUserLegalEntityInfo.legalEntityRole ?? '') + ")" );
        } else if (userInfo.citizenInfo && userInfo.citizenInfo.name && userInfo.citizenInfo.surname) {
            this.headerInformation.set( (userInfo.citizenInfo.name.substring(0, 1)) + ". " + userInfo.citizenInfo.surname);
        }
    }

    /*********************************************
     * HTTP CLIENT METHODS
     *********************************************/

    signup(accountInfo: AccountInfo): Promise<boolean> {
        // console.log("PermissionService.signup: calling the following URL: " + `${PermissionService.authBaseUrl}`);
        // console.info("PermissionService.signup: accountInfo: " + JSON.stringify(accountInfo));
        return lastValueFrom(this.httpClient.post<boolean>(`${PermissionService.authBaseUrl}`, accountInfo));
    }

    signin(accountInfo: AccountInfo): Promise<UserInfo> {
        return lastValueFrom(this.httpClient.patch<UserInfo>(`${PermissionService.authBaseUrl}`, {'accountInfo': accountInfo})).then( (result: UserInfo) => {
            if (SharedUtils.inputValueIsPresent(result)) {
                this.login(result);
                // console.log("PermissionService.signin() - Return value: " + JSON.stringify(result) );
                return result;
            } else {
                throw new Error("Somethig whent wrong");
            }
        });
    }

    signout(): Promise<boolean> {
        return lastValueFrom(this.httpClient.get<boolean>(`${PermissionService.authBaseUrl}/logout`)).then( (result: boolean) => {
            this.logout(result);
            return !!result;
        }).catch( (error) => {
            console.error("PermissionService.signout - Error during signout: " + error);
            this.logout(false);
            return false;
        }).finally( () => {
            SharedUtils.cleanModals();
            this.setWaitingSpinner(false);
            this.router.navigate(['/']);
        });
    }

    forgotPassword(userEmail: string): Promise<boolean> {
        // return lastValueFrom(this.httpClient.patch<boolean>(`${PermissionService.authBaseUrl}/forgotPassword`, {email: userEmail}, {responseType: ("boolean" as "json")}));
        return lastValueFrom(this.httpClient.patch<boolean>(`${PermissionService.authBaseUrl}/forgotPassword`, {email: userEmail}));
    }

    forgotPasswordAccount(token: string): Promise<AccountInfo> {
        return lastValueFrom(this.httpClient.patch<AccountInfo>(`${PermissionService.authBaseUrl}/forgotPasswordAccount`, {"token": token}));
    }

    forgotPasswordChange(accountInfo: AccountInfo, token: string): Promise<boolean> {
        return lastValueFrom(this.httpClient.put<boolean>(`${PermissionService.authBaseUrl}/forgotPasswordChange`, {"accountInfo": accountInfo, "token": token}));
    }

    emailChangeActivate(token: string): Promise<AccountInfo> {
        return lastValueFrom(this.httpClient.patch<AccountInfo>(`${PermissionService.authBaseUrl}/emailChangeActivate`, {"token": token}));
    }

    activateAccount(token: string): Promise<AccountInfo> {
        return lastValueFrom(this.httpClient.patch<AccountInfo>(`${PermissionService.authBaseUrl}/activateAccount`, {"token": token}));
    }

    checkEmailAvailable(email: string): Promise<boolean> {
        // return lastValueFrom(this.httpClient.get<boolean>(`${PermissionService.authBaseUrl}/emailAvailable`, { params: { 'email': email }, responseType: ("boolean" as "json") }));
        return lastValueFrom(this.httpClient.get<boolean>(`${PermissionService.authBaseUrl}/emailAvailable`, { params: { 'email': email } }));
    }

    checkNicknameAvailable(nickname: string): Promise<boolean> {
        // return lastValueFrom(this.httpClient.get<boolean>(`${PermissionService.authBaseUrl}/nicknameAvailable`, { params: { 'nickname': nickname }, responseType: ("boolean" as "json") }));
        return lastValueFrom(this.httpClient.get<boolean>(`${PermissionService.authBaseUrl}/nicknameAvailable`, { params: { 'nickname': nickname } }));
    }

    sendRegistrationConfirmMail(nickname: string): Promise<any> {
        return lastValueFrom(this.httpClient.patch<string>(`${PermissionService.authBaseUrl}/registrationConfirmMail`, {nickname: nickname}, { responseType: 'text' } as any));
    }

    /********************************************************/

    /******************************************************
    public getClientUserInfoId(): number {
        return this.cookieService.getClientUserId() ?? 0;
    }

    public getClientUserInfoEmail(): string | undefined {
        return this.cookieService.getClientUserEmail();
    }

    public getClientUserInfoStatusId(): number {
        return this.cookieService.getClientUserStatusId() ?? 0;
    }
  
    public getClientUserInfoNickname(): string | undefined {
        return this.cookieService.getClientUserNickname();
    }

    public getClientUserInfoRoles(): Array<string> | undefined {
        return this.cookieService.getClientUserRoles();
    }

    public getClientCitizenInfoFullName(): string | undefined {
        return this.cookieService.getClientCitizenFullName();
    }
    ********************************************************/
}
